import type { PlatformMessagingNotice } from '@exylon/generated-contract-kernel/types';

import { formatCount } from '../count';
import type { NoticeReceiptAction } from './contract-port';
import { groupNotices, noticeDismissReason, noticeInboxHeading, noticeRecipientState, noticeStateLine, noticeTime } from './notice-model';
import { isCanonicalUuid } from '../../runtime/format';
import './notice-list.scss';

const NOTICE_TITLES: Readonly<Record<string, string>> = Object.freeze({
  'identity.access-policy.publish': 'Política de acceso publicada',
  'identity.access-policy.restore': 'Política de acceso restaurada',
});

/** Known first-party categories use the approved Spanish presentation; unknown categories retain their canonical title. */
function noticeTitle(notice: PlatformMessagingNotice): string {
  return NOTICE_TITLES[notice.category] ?? notice.title;
}

/** Only the edition producer has a destination: the category selects the fixed Home route, never a payload URL. */
const parteEditionNotice = (category: string): boolean => category.startsWith('parte.edition.')
  && isCanonicalUuid(category.slice('parte.edition.'.length));

export interface NoticeInboxProjection {
  readonly status: 'loading' | 'ready' | 'error';
  readonly items: readonly PlatformMessagingNotice[];
  readonly complete: boolean;
  /** The Backend's exact unread total of the whole inbox. */
  readonly unreadCount: number | null;
  readonly error?: string;
}

const SEVERITY = { critical: 'crítico', warning: 'aviso', info: 'informativo', success: 'correcto' } as const;
const ACTIONS = [{ action: 'seen', label: 'Visto', state: 'seen' }, { action: 'read', label: 'Leído', state: 'read' }, { action: 'dismiss', label: 'Descartar', state: 'dismissed' }] as const;

export interface NoticeListProps {
  readonly now: number;
  readonly inbox: NoticeInboxProjection;
  readonly onRetry?: (() => void) | undefined;
  /** Reads one more page of older notices; absent when none can be loaded now. */
  readonly onLoadOlder?: (() => void) | undefined;
  readonly onNavigate?: ((path: string) => void) | undefined;
  readonly onReceipt?: ((noticeId: string, action: NoticeReceiptAction) => void) | undefined;
  /** Records one action on every copy of a repeated notice at once; without it each copy goes through `onReceipt`. */
  readonly onReceiptMany?: ((noticeIds: readonly string[], action: NoticeReceiptAction) => void) | undefined;
  readonly actionPermissions?: Readonly<Record<NoticeReceiptAction, boolean>> | undefined;
  readonly pendingNotices?: readonly string[] | undefined;
  readonly receiptErrors?: Readonly<Record<string, string>> | undefined;
  readonly disabledReason?: string | undefined;
}

/** The recipient's notices, opened from the identity chrome on every page. */
export function NoticeList({ now, inbox, onRetry, onLoadOlder, onNavigate, onReceipt, onReceiptMany,
  actionPermissions, pendingNotices = [], receiptErrors = {}, disabledReason }: NoticeListProps) {
  const fresh = inbox.status === 'ready';
  return <section aria-labelledby="notice-inbox-list-heading" className="notice-list__inbox" data-inbox-anchor="" data-testid="notice-inbox-list" id="notice-inbox-list" tabIndex={-1}>
      <header className="notice-list__inbox-header">
        <h2 id="notice-inbox-list-heading">Avisos</h2>
        <span aria-live="polite" className="notice-list__count" data-testid="notice-inbox-list-count">{fresh && inbox.unreadCount !== null ? noticeInboxHeading(inbox.items, inbox.complete, inbox.unreadCount) : inbox.status === 'loading' ? 'Cargando…' : 'Sin datos'}</span>
      </header>
      <div className="notice-list__inbox-body">
      {disabledReason && <p className="notice-list__status" role="status">{disabledReason}</p>}
      {inbox.status === 'loading' && <p className="notice-list__status" role="status">Cargando tus avisos…</p>}
      {inbox.status === 'error' && <div className="notice-list__status" role="alert">
        <p>{inbox.error ?? 'No se han podido cargar tus avisos.'}</p>
        {!!inbox.items.length && <p>Última lectura disponible. Los cambios esperan una lectura actualizada.</p>}
        {onRetry && <button data-testid="notice-inbox-list-retry" onClick={onRetry} type="button">Reintentar</button>}
      </div>}
      {fresh && inbox.complete && !inbox.items.length && <div className="notice-list__empty-state">
        <span className="notice-list__empty-mark"><span aria-hidden="true">✓</span>Nada pendiente</span>
        <h3>No tienes avisos</h3>
      </div>}
      {groupNotices(inbox.items).map(({ lead, members }) => {
        const notice = lead;
        const ids = members.map((member) => member.id);
        const repeated = members.length > 1;
        const state = noticeRecipientState(notice);
        const dismissReason = noticeDismissReason(notice);
        const pending = ids.some((id) => pendingNotices.includes(id));
        const receiptError = ids.map((id) => receiptErrors[id]).find(Boolean);
        const oldest = members[members.length - 1]!;
        const receipt = (action: NoticeReceiptAction) => {
          if (repeated && onReceiptMany) onReceiptMany(ids, action);
          else for (const id of ids) onReceipt?.(id, action);
        };
        const unavailable = onReceipt === undefined && (!repeated || onReceiptMany === undefined);
        return <article className="inbox-notice" data-notice-count={members.length} data-notice-id={notice.id} data-state={state} data-tone={notice.severity} key={notice.id}>
          <div className="inbox-notice__top">
            <span className="inbox-notice__severity">{SEVERITY[notice.severity]}</span>
            {repeated && <span aria-label={String(members.length) + ' avisos iguales'} className="inbox-notice__repeat" data-testid="inbox-notice-repeat">{'×' + String(members.length)}</span>}
            <time dateTime={notice.createdAt}>{noticeTime(notice.createdAt, now)}</time>
          </div>
          <h3 className="inbox-notice__title">{noticeTitle(notice)}</h3>
          <div className="inbox-notice__meta">
            <span className="inbox-notice__state">{noticeStateLine(notice)}</span>
            <span className="inbox-notice__copy">{repeated
              ? 'Tus ' + (formatCount(members.length) ?? String(members.length)) + ' copias · la primera ' + noticeTime(oldest.createdAt, now)
              : 'Tu copia'}</span>
          </div>
          <div className="inbox-notice__actions">
            {parteEditionNotice(notice.category) && <button className="btn btn--secondary" disabled={!fresh || !!disabledReason || onNavigate === undefined}
              onClick={() => onNavigate?.('/app/home')} type="button">Abrir el parte</button>}
            {ACTIONS.map(({ action, label, state: selectedState }) => {
              const reason = disabledReason || (!fresh ? 'Es necesaria una lectura actualizada.' : pending ? 'Guardando el estado de tu copia…'
                : unavailable ? 'La operación de avisos no está conectada.'
                  : actionPermissions?.[action] === false ? 'No tienes permiso para esta acción.' : action === 'dismiss' ? dismissReason : null);
              const selected = state === selectedState;
              return <button data-testid="notice-receipt" aria-describedby={reason ? 'notice-reason-' + notice.id : undefined} aria-pressed={selected} disabled={Boolean(reason) || selected}
                key={action} onClick={() => receipt(action)} title={reason ?? ''} type="button">{label}</button>;
            })}
          </div>
          {(dismissReason || disabledReason || pending || !fresh || unavailable || ACTIONS.some(({ action }) => actionPermissions?.[action] === false)) && <p className="inbox-notice__reason" id={'notice-reason-' + notice.id}
            hidden={fresh && !disabledReason && !pending && !unavailable && !ACTIONS.some(({ action }) => actionPermissions?.[action] === false)
              && (notice.severity === 'critical' || notice.severity === 'warning')}>
            {disabledReason || (!fresh ? 'Es necesaria una lectura actualizada.' : pending ? 'Guardando el estado de tu copia…'
              : unavailable ? 'La operación de avisos no está conectada.'
                : ACTIONS.some(({ action }) => actionPermissions?.[action] === false) ? 'No tienes permiso para alguna de las acciones.' : dismissReason)}
          </p>}
          {receiptError && <p className="inbox-notice__error" role="alert">{receiptError}</p>}
        </article>;
      })}
      {fresh && !inbox.complete
        && <div className="notice-list__older" data-inbox-older="">
          <span>{'Los ' + (formatCount(inbox.items.length) ?? String(inbox.items.length)) + ' más recientes'}</span>
          {onLoadOlder && !disabledReason && <button data-testid="notice-inbox-list-older" onClick={onLoadOlder} type="button">Cargar avisos anteriores</button>}
        </div>}
      </div>
  </section>;
}
